/**
 * schema.org JSON-LD builders for the marketing site.
 *
 * Two citation signals AI answer engines and search actually read:
 *  - a site-wide `SoftwareApplication` describing what FrazzleCRM is and
 *    what it costs (rendered on every marketing page through SiteShell), and
 *  - per-page `HowTo` blocks that mirror steps a page already shows.
 *
 * Hard rule: every value here must match visible page content and stay
 * honest — schema.org and Google both require the match, and prices track
 * src/pages/site/pricing.tsx exactly (Free $0 / Starter $29 / Pro $79 /
 * Scale $199) plus the one-time Founding Member offer ($397, 100 spots,
 * src/lib/foundingOffer.ts). If either changes, change this too.
 */

export const SITE_URL = "https://www.frazzlecrm.com";

/**
 * The site-wide product schema. AggregateOffer spans every public offer —
 * the four subscription tiers and the one-time Founding Member price — so an
 * assistant can answer "how much is FrazzleCRM" from one blob. Leaving the
 * highest-priced public offer out would make the range quietly wrong.
 */
export function softwareApplicationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FrazzleCRM",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "CRM",
    operatingSystem: "Web",
    url: SITE_URL,
    description:
      "FrazzleCRM is a CRM where the funnel map IS the automation: draw the " +
      "funnel and every step becomes a live email/SMS automation. Free tier, " +
      "AI access via MCP, and flat pricing with no per-contact billing.",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "0",
      highPrice: "397",
      offerCount: "5",
      offers: [
        { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
        { "@type": "Offer", name: "Starter", price: "29", priceCurrency: "USD" },
        { "@type": "Offer", name: "Pro", price: "79", priceCurrency: "USD" },
        { "@type": "Offer", name: "Scale", price: "199", priceCurrency: "USD" },
        {
          "@type": "Offer",
          name: "Founding Member (one-time, limited to 100)",
          price: "397",
          priceCurrency: "USD",
          url: `${SITE_URL}/founding-members`,
        },
      ],
    },
    featureList: [
      "Visual funnel map that compiles to real automations",
      "Two-way sync between the map and the automation editor",
      "Email & SMS sequences with your own credentials",
      "Templates, appointment reminders, import/export",
      "AI access via MCP (Model Context Protocol)",
    ],
  } as const;
}

/**
 * The Founding Member offer as a `Product` + `Offer` — the shape an assistant
 * reads to answer "is there a lifetime/one-time deal for FrazzleCRM?".
 *
 * `inventoryLevel` is the live remaining count from the same server render
 * that draws the counter on the page, and `availability` flips to SoldOut with
 * it, so the schema can never advertise a spot the page says is gone.
 */
export function foundingOfferLd(offer: {
  price: number;
  currency: string;
  total: number;
  remaining: number;
  soldOut: boolean;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "FrazzleCRM Founding Member",
    url: `${SITE_URL}/founding-members`,
    brand: { "@type": "Brand", name: "FrazzleCRM" },
    category: "CRM and marketing automation software",
    description:
      `One payment of $${offer.price} for FrazzleCRM Founding Member access, with no ` +
      `recurring FrazzleCRM subscription. Limited to ${offer.total} members. Email and ` +
      "SMS still send through your own providers, billed by them directly.",
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/founding-members`,
      price: String(offer.price),
      priceCurrency: offer.currency.toUpperCase(),
      availability: offer.soldOut
        ? "https://schema.org/SoldOut"
        : "https://schema.org/InStock",
      inventoryLevel: {
        "@type": "QuantitativeValue",
        value: offer.remaining,
        maxValue: offer.total,
        unitText: "Founding Member spots",
      },
      seller: { "@type": "Organization", name: "FrazzleCRM", url: SITE_URL },
    },
  };
}

/**
 * `FAQPage` — the format AI answer engines and rich results both read.
 * Questions and answers must be ones the page actually renders.
 */
export function faqLd(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * `DefinedTerm` for one glossary entry. `description` is the page's own
 * one-or-two-sentence definition — the string an assistant quotes — so it
 * has to stay identical to the visible lede.
 */
export function definedTermLd(t: {
  slug: string;
  term: string;
  definition: string;
  aka?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": `${SITE_URL}/learn/${t.slug}`,
    name: t.term,
    description: t.definition,
    url: `${SITE_URL}/learn/${t.slug}`,
    ...(t.aka?.length ? { alternateName: t.aka } : {}),
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      "@id": `${SITE_URL}/learn`,
      name: "FrazzleCRM CRM & marketing automation glossary",
      url: `${SITE_URL}/learn`,
    },
  };
}

/** `DefinedTermSet` for the /learn hub, listing every term it contains. */
export function definedTermSetLd(terms: { slug: string; term: string; definition: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": `${SITE_URL}/learn`,
    name: "FrazzleCRM CRM & marketing automation glossary",
    description:
      "Plain-English definitions of the CRM and marketing-automation terms that decide whether a funnel works — triggers, waits, goals, stop tags, send windows, trigger links and more.",
    url: `${SITE_URL}/learn`,
    hasDefinedTerm: terms.map((t) => ({
      "@type": "DefinedTerm",
      "@id": `${SITE_URL}/learn/${t.slug}`,
      name: t.term,
      description: t.definition,
      url: `${SITE_URL}/learn/${t.slug}`,
    })),
  };
}

type HowToStep = { name: string; text: string };

/** A `HowTo` blob whose steps must match ones the page renders. */
export function howToLd(name: string, description: string, steps: HowToStep[]) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/**
 * "How to embed a funnel map" — mirrors the three numbered steps rendered on
 * /embed. Change one, change the other.
 */
export function embedHowToLd() {
  return howToLd(
    "How to embed a live funnel map on your website",
    "Turn any FrazzleCRM funnel map into a live, pannable diagram inside your own page with a free iframe embed.",
    [
      {
        name: "Share the map",
        text: "Open the map in FrazzleCRM and hit Share — that creates a public read-only link. Automation content stays private.",
      },
      {
        name: "Copy the embed code",
        text: "The Share popover hands you a ready-made iframe snippet pointing at /m/<token>/embed.",
      },
      {
        name: "Paste it into your page",
        text: "Drop the iframe into any HTML, blog post or docs page. Visitors can pan and zoom the live map; it updates when you edit the map.",
      },
    ]
  );
}

/**
 * "How to run a sales call from a script" — mirrors the four numbered steps
 * rendered on /sales-scripts. Change one, change the other.
 */
export function salesScriptHowToLd() {
  return howToLd(
    "How to run a sales call from a branching script in FrazzleCRM",
    "Build a call script as a flow chart on your funnel map, then run a live call from it so the route the conversation took is recorded against the contact.",
    [
      {
        name: "Drop a Sales Script on the map",
        text: "Add a Sales Script container to your funnel map. It sits on the same canvas as the automations that feed and follow the call.",
      },
      {
        name: "Fill it with cards",
        text: "Add Question, Statement, Transition, Action and Context cards — one move each — and wire them in the order the call should go.",
      },
      {
        name: "Label the branches",
        text: "Draw an edge out of a question for each common answer and label it (\"Yes\", \"Too expensive\", \"Not the decision maker\"). The label is the route the call took.",
      },
      {
        name: "Press Run on a live call",
        text: "Start a run against the contact you are calling and click each card as you use it. Ending the run saves the full path to the contact and fires the sales-script-ends automation trigger.",
      },
    ]
  );
}

/**
 * "How to use this template" — mirrors the "How cloning works" list every
 * template page renders. Same three steps, so the schema stays truthful.
 */
export function templateHowToLd(templateName: string) {
  return howToLd(
    `How to use the ${templateName} template in FrazzleCRM`,
    `Clone the free ${templateName} funnel template into FrazzleCRM and turn every step into a live automation.`,
    [
      {
        name: "Create a free account",
        text: "Sign up for FrazzleCRM free — no credit card required.",
      },
      {
        name: "Clone the template",
        text: "The template lands on your canvas as a real map — every step, wait and branch already wired.",
      },
      {
        name: "Make it yours and go live",
        text: "Swap in your copy and senders, then flip the flow live. The map IS the automation.",
      },
    ]
  );
}
